import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import _ from 'lodash'
import format from 'date-fns/format'
import addMonths from 'date-fns/add_months';
import subMonths from 'date-fns/sub_months';
import addDays from "date-fns/add_days";
import addWeeks from 'date-fns/add_weeks'
import startOfQuarter from 'date-fns/start_of_quarter'
import addQuarters from 'date-fns/add_quarters'
import { subDays } from 'date-fns';
import axios from 'axios';
import { getCompany } from './core/cacheService'
import { pgSummary } from './pgReadApi'
import { getCompanyTimezone, toCompanyDate } from './utils/companyTimezone'
const ALL_TEAMS_AND_COHORTS = 'All Teams and Cohorts'
const DAY_IN_MS = 24 * 60 * 60 * 1000
const DURATION_CACHE_TTL_MS = 30 * 1000
const herepointChunkRequests: { [company: string]: Promise<Array<any>> } = {}
const durationRecognitionMonthCache: { [key: string]: { fetchedAt: number; records: Array<any> } } = {}
const durationRecognitionMonthRequests: { [key: string]: Promise<Array<any>> } = {}
const durationSummaryMonthCache: { [key: string]: { fetchedAt: number; records: Array<any> } } = {}
const durationSummaryMonthRequests: { [key: string]: Promise<Array<any>> } = {}
const classroomsGiftCache: { [company: string]: Array<any> } = {}
const classroomsGiftRequests: { [company: string]: Promise<Array<any>> } = {}

const createDateFromDayKey = (dayKey: string): Date => {
  const [year, month, day] = `${dayKey || ''}`.slice(0, 10).split('-').map(Number)
  return new Date(year || 1970, (month || 1) - 1, day || 1)
}

const toDayKey = (value: any): string => {
  if (!value) {
    return ''
  }
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10)
    }
    if (/^\d{4}-\d{2}$/.test(value)) {
      return `${value}-01`
    }
  }
  return format(new Date(value), 'YYYY-MM-DD')
}

const toMonthKey = (value: any): string => {
  const dayKey = toDayKey(value)
  return dayKey ? dayKey.slice(0, 7) : ''
}

const getMonthStartKey = (monthKey: string): string => {
  return `${monthKey}-01`
}

const getMonthEndKey = (monthKey: string): string => {
  const [year, month] = monthKey.split('-').map(Number)
  return format(new Date(year, month, 0), 'YYYY-MM-DD')
}

const addDaysToKey = (dayKey: string, amount: number): string => {
  return format(addDays(createDateFromDayKey(dayKey), amount), 'YYYY-MM-DD')
}

const getInclusiveDayCount = (startDay: string, endDay: string): number => {
  const diff = createDateFromDayKey(endDay).getTime() - createDateFromDayKey(startDay).getTime()
  return Math.max(1, Math.round(diff / DAY_IN_MS) + 1)
}

const buildDayKeyRange = (startDay: string, endDay: string): Array<string> => {
  const totalDays = getInclusiveDayCount(startDay, endDay)
  const dayKeys: Array<string> = []
  for (let i = 0; i < totalDays; i++) {
    dayKeys.push(addDaysToKey(startDay, i))
  }
  return dayKeys
}

const buildMonthlyQueryChunks = (startDay: string, endDay: string): Array<{ start: string; end: string }> => {
  const chunks: Array<{ start: string; end: string }> = []
  let currentMonth = toMonthKey(startDay)
  const endMonth = toMonthKey(endDay)

  while (currentMonth && currentMonth <= endMonth) {
    const chunkStart = currentMonth === toMonthKey(startDay) ? startDay : getMonthStartKey(currentMonth)
    const chunkEnd = currentMonth === endMonth ? endDay : getMonthEndKey(currentMonth)
    chunks.push({
      start: `${chunkStart} 00:00`,
      end: `${chunkEnd} 23:59`,
    })
    currentMonth = toMonthKey(addDaysToKey(getMonthEndKey(currentMonth), 1))
  }

  return chunks
}

const buildMonthKeysInRange = (startDay: string, endDay: string): Array<string> => {
  const monthKeys: Array<string> = []
  let currentMonth = toMonthKey(startDay)
  const finalMonth = toMonthKey(endDay)

  while (currentMonth && currentMonth <= finalMonth) {
    monthKeys.push(currentMonth)
    currentMonth = toMonthKey(addDaysToKey(getMonthEndKey(currentMonth), 1))
  }

  return monthKeys
}

const buildLastTwelveMonthKeys = (): Array<string> => {
  const monthKeys: Array<string> = []
  for (let i = 11; i >= 0; i--) {
    monthKeys.push(format(subMonths(new Date(), i), 'YYYY-MM'))
  }
  return monthKeys
}

const getUniqueUserKey = (entry: any): string => {
  if (typeof entry === 'string') {
    return normalizeEmail(entry)
  }
  if (entry && entry.email) {
    return normalizeEmail(entry.email)
  }
  return ''
}

const countUniqueUsers = (entries: Array<any>): number => {
  const uniqueUsers = new Set<string>()
  ;(entries || []).forEach((entry: any) => {
    const key = getUniqueUserKey(entry)
    if (key) {
      uniqueUsers.add(key)
    }
  })
  return uniqueUsers.size
}

const collectUsersForDays = (userMap: { [key: string]: Array<any> }, dayKeys: Array<string>): Array<any> => {
  const users: Array<any> = []
  dayKeys.forEach((dayKey) => {
    if (Array.isArray(userMap[dayKey])) {
      users.push(...userMap[dayKey])
    }
  })
  return users
}

const buildDurationCacheKey = (company: string, collectionName: string, monthKey: string): string => {
  return `${company}::${collectionName}::${monthKey}`
}

const isDurationCacheFresh = (entry: any): boolean => {
  return !!entry && Date.now() - entry.fetchedAt < DURATION_CACHE_TTL_MS
}

const getMonthQueryRange = (monthKey: string): { start: string; end: string } => {
  const now = new Date()
  return {
    start: `${getMonthStartKey(monthKey)} 00:00`,
    end: monthKey === format(now, 'YYYY-MM')
      ? `${format(now, 'YYYY-MM-DD')} 23:59`
      : `${getMonthEndKey(monthKey)} 23:59`,
  }
}

const clearCompanyDurationCaches = (company: string): void => {
  const prefix = `${company}::`
  ;[
    durationRecognitionMonthCache,
    durationRecognitionMonthRequests,
    durationSummaryMonthCache,
    durationSummaryMonthRequests,
  ].forEach((cacheBucket: any) => {
    Object.keys(cacheBucket).forEach((cacheKey) => {
      if (cacheKey.startsWith(prefix)) {
        delete cacheBucket[cacheKey]
      }
    })
  })
  delete classroomsGiftCache[company]
  delete classroomsGiftRequests[company]
}

const flattenRecordBatches = (batches: Array<Array<any>>): Array<any> => {
  const merged: Array<any> = []
  batches.forEach((records) => {
    if (Array.isArray(records)) {
      merged.push(...records)
    }
  })
  return merged
}

const fetchDurationRecognitionMonth = async (company: string, monthKey: string): Promise<Array<any>> => {
  const cacheKey = buildDurationCacheKey(company, 'here_point', monthKey)
  const cachedEntry = durationRecognitionMonthCache[cacheKey]
  if (isDurationCacheFresh(cachedEntry)) {
    return cachedEntry.records
  }
  if (durationRecognitionMonthRequests[cacheKey]) {
    return durationRecognitionMonthRequests[cacheKey]
  }

  durationRecognitionMonthRequests[cacheKey] = (async () => {
    const { start, end } = getMonthQueryRange(monthKey)
    const records: Array<any> = []
    const snapshot = await firebase.firestore().collection('here_point')
      .where('company', '==', company)
      .where('statusrow', '==', 'sendcoin')
      .where('date', '>=', start)
      .where('date', '<=', end)
      .get()

    snapshot.forEach((doc: any) => {
      const data = doc.data()
      data.id = doc.id
      records.push(data)
    })

    durationRecognitionMonthCache[cacheKey] = {
      fetchedAt: Date.now(),
      records,
    }
    return records
  })()

  try {
    return await durationRecognitionMonthRequests[cacheKey]
  } finally {
    delete durationRecognitionMonthRequests[cacheKey]
  }
}

const fetchDurationRecognitionRange = async (company: string, startDay: string, endDay: string): Promise<Array<any>> => {
  const monthKeys = buildMonthKeysInRange(startDay, endDay)
  return flattenRecordBatches(await Promise.all(
    monthKeys.map((monthKey) => fetchDurationRecognitionMonth(company, monthKey))
  ))
}

const fetchDurationSummaryMonth = async (
  company: string,
  collectionName: string,
  monthKey: string
): Promise<Array<any>> => {
  const cacheKey = buildDurationCacheKey(company, collectionName, monthKey)
  const cachedEntry = durationSummaryMonthCache[cacheKey]
  if (isDurationCacheFresh(cachedEntry)) {
    return cachedEntry.records
  }
  if (durationSummaryMonthRequests[cacheKey]) {
    return durationSummaryMonthRequests[cacheKey]
  }

  durationSummaryMonthRequests[cacheKey] = (async () => {
    const { start, end } = getMonthQueryRange(monthKey)
    let records: Array<any> = []
    if (collectionName === 'summary_active_users') {
      // PG Read API: per-day active-user summary (replaces summary_active_users docs).
      const days = await pgSummary(start.slice(0, 10), end.slice(0, 10), company)
      records = days || []
    } else {
      const snapshot = await firebase.firestore().collection(collectionName)
        .where('company', '==', company)
        .where('date', '>=', start.slice(0, 10))
        .where('date', '<=', end.slice(0, 10))
        .get()
      snapshot.forEach((doc: any) => {
        records.push(doc.data())
      })
    }

    durationSummaryMonthCache[cacheKey] = {
      fetchedAt: Date.now(),
      records,
    }
    return records
  })()

  try {
    return await durationSummaryMonthRequests[cacheKey]
  } finally {
    delete durationSummaryMonthRequests[cacheKey]
  }
}

const fetchDurationSummaryRange = async (
  company: string,
  collectionName: string,
  startDay: string,
  endDay: string
): Promise<Array<any>> => {
  const monthKeys = buildMonthKeysInRange(startDay, endDay)
  return flattenRecordBatches(await Promise.all(
    monthKeys.map((monthKey) => fetchDurationSummaryMonth(company, collectionName, monthKey))
  ))
}

const fetchClassroomsGiftCached = async (company: string): Promise<Array<any>> => {
  if (classroomsGiftCache[company]) {
    return classroomsGiftCache[company]
  }
  if (classroomsGiftRequests[company]) {
    return classroomsGiftRequests[company]
  }

  classroomsGiftRequests[company] = (async () => {
    const gifts: Array<any> = []
    const snapshot = await firebase.firestore().collection('classrooms_gift')
      .where('company', '==', company)
      .get()

    snapshot.forEach((doc: any) => {
      gifts.push(doc.data())
    })
    classroomsGiftCache[company] = gifts
    return gifts
  })()

  try {
    return await classroomsGiftRequests[company]
  } finally {
    delete classroomsGiftRequests[company]
  }
}

const calculatePercentage = (numerator: number, denominator: number, decimals: number = 0): string => {
  if (!denominator) {
    return '0'
  }
  const value = (numerator * 100) / denominator
  return Number.isFinite(value) ? value.toFixed(decimals) : '0'
}

const calculateChange = (currentValue: number, previousValue: number): string => {
  if (!previousValue) {
    return '0'
  }
  const value = ((currentValue - previousValue) * 100) / previousValue
  return Number.isFinite(value) ? value.toFixed(0) : '0'
}

const createEmptyOverviewData = () => ({
  totalRecognition: 0,
  chartRecognition: [],
  labels: [],
  pasttotalRecognition: 0,
  compareRecognition: '0',
  value_aligned: 0,
  pastvalue_aligned: 0,
  comparevalue_aligned: '0',
  chartvalue_aligned: [],
  percent_value_aligned: '0',
  comparepercent_value_aligned: '0',
  chartpercent_value_aligned: [],
  participation: '0',
  chartparticipation: [],
  past_participation: '0',
})

const resolveMetricBucketStrategy = (time: string, totalDays: number): string => {
  if (time === 'Past 30 days') {
    return 'day'
  }
  if (time === 'Past 90 days' || time === 'This Quarter' || time === 'Last Quarter') {
    return 'week'
  }
  if (time === 'Past 180 days') {
    return 'biweek'
  }
  if (time === 'Past year') {
    return 'month'
  }
  if (time === 'Custom Range') {
    if (totalDays <= 45) {
      return 'day'
    }
    if (totalDays <= 180) {
      return 'week'
    }
    return 'month'
  }
  return totalDays <= 45 ? 'day' : 'month'
}

const buildMetricBuckets = (time: string, startDay: string, endDay: string): Array<{ label: string; days: Array<string> }> => {
  const dayKeys = buildDayKeyRange(startDay, endDay)
  const strategy = resolveMetricBucketStrategy(time, dayKeys.length)

  if (strategy === 'day') {
    return dayKeys.map((dayKey) => ({
      label: dayKey,
      days: [dayKey],
    }))
  }

  if (strategy === 'month') {
    const monthMap: { [key: string]: Array<string> } = {}
    dayKeys.forEach((dayKey) => {
      const monthKey = dayKey.slice(0, 7)
      if (!monthMap[monthKey]) {
        monthMap[monthKey] = []
      }
      monthMap[monthKey].push(dayKey)
    })
    return Object.keys(monthMap).map((monthKey) => ({
      label: monthKey,
      days: monthMap[monthKey],
    }))
  }

  if (strategy === 'week') {
    const weekMap: { [key: string]: Array<string> } = {}
    dayKeys.forEach((dayKey) => {
      const weekKey = format(createDateFromDayKey(dayKey), 'YYYY-W')
      if (!weekMap[weekKey]) {
        weekMap[weekKey] = []
      }
      weekMap[weekKey].push(dayKey)
    })
    return Object.keys(weekMap).map((weekKey) => ({
      label: weekKey,
      days: weekMap[weekKey],
    }))
  }

  const buckets: Array<{ label: string; days: Array<string> }> = []
  for (let i = 0; i < dayKeys.length; i += 14) {
    const bucketDays = dayKeys.slice(i, i + 14)
    buckets.push({
      label: bucketDays[0],
      days: bucketDays,
    })
  }
  return buckets
}

const buildRecognitionDailyTotals = (records: Array<any>): { [key: string]: { activity: number; hashtag_value: number } } => {
  const recognitionByDay: { [key: string]: { activity: number; hashtag_value: number } } = {}
  records.forEach((record: any) => {
    const dayKey = toDayKey(record && record.date ? record.date : '')
    if (!dayKey) {
      return
    }
    if (!recognitionByDay[dayKey]) {
      recognitionByDay[dayKey] = {
        activity: 0,
        hashtag_value: 0,
      }
    }
    recognitionByDay[dayKey].activity++
    if (record && Array.isArray(record.hashtag) && record.hashtag.length > 0) {
      recognitionByDay[dayKey].hashtag_value++
    }
  })
  return recognitionByDay
}

const buildSummaryUserMap = (source: any, allowedEmailSet: Set<string> | null): { [key: string]: Array<any> } => {
  const dailyUsers: { [key: string]: Array<any> } = {}
  const records = Array.isArray(source)
    ? source
    : []

  if (Array.isArray(records) && records.length > 0) {
    records.forEach((data: any) => {
      const dayKey = toDayKey(data && data.date ? data.date : '')
      if (!dayKey) {
        return
      }
      const sourceUsers = Array.isArray(data && data.users) ? data.users : []
      const scopedUsers = filterUsersByScope(sourceUsers, allowedEmailSet)
      if (!dailyUsers[dayKey]) {
        dailyUsers[dayKey] = []
      }
      dailyUsers[dayKey].push(...scopedUsers)
    })
    return dailyUsers
  }

  if (!source || typeof source.forEach !== 'function') {
    return dailyUsers
  }

  source.forEach((doc: any) => {
    const data = typeof doc.data === 'function' ? doc.data() : doc
    const dayKey = toDayKey(data && data.date ? data.date : '')
    if (!dayKey) {
      return
    }
    const sourceUsers = Array.isArray(data && data.users) ? data.users : []
    const scopedUsers = filterUsersByScope(sourceUsers, allowedEmailSet)
    if (!dailyUsers[dayKey]) {
      dailyUsers[dayKey] = []
    }
    dailyUsers[dayKey].push(...scopedUsers)
  })
  return dailyUsers
}

const normalizeEmail = (email: any): string => {
  if (!email || typeof email !== 'string') {
    return ''
  }
  return email.trim().toLowerCase()
}

const decodeEmailKey = (emailKey: string): string => {
  return emailKey.replace(/\|\|/g, '.')
}

const resolveDateRange = (value: any): { startAt: string; endAt: string } => {
  if (value && value.startDate && value.endDate) {
    const startDate = typeof value.startDate === 'string'
      ? value.startDate
      : format(new Date(value.startDate), 'YYYY-MM-DD')
    const endDate = typeof value.endDate === 'string'
      ? value.endDate
      : format(new Date(value.endDate), 'YYYY-MM-DD')
    return {
      startAt: `${startDate} 00:00`,
      endAt: `${endDate} 23:59`,
    }
  }

  if (value && value.start && value.end && typeof value.start === 'string' && typeof value.end === 'string' && value.start.includes('-') && value.end.includes('-')) {
    const startDate = value.start.slice(0, 10)
    const endDate = value.end.slice(0, 10)
    return {
      startAt: `${startDate} 00:00`,
      endAt: `${endDate} 23:59`,
    }
  }

  const monthValue = value && value.value ? value.value : format(new Date(), 'YYYY-MM')
  return {
    startAt: `${monthValue}-01 00:00`,
    endAt: `${getMonthEndKey(monthValue)} 23:59`,
  }
}

const getScopeType = (scopeKey: string, rootState: any): 'all' | 'team' | 'cohort' => {
  if (!scopeKey || scopeKey === ALL_TEAMS_AND_COHORTS) {
    return 'all'
  }
  if (rootState && rootState.main && rootState.main.allTeam && rootState.main.allTeam[scopeKey]) {
    return 'team'
  }
  if (rootState && rootState.leaderboards && rootState.leaderboards.cohortUser && rootState.leaderboards.cohortUser[scopeKey]) {
    return 'cohort'
  }
  return 'all'
}

const getLeadershipScopedScopeKey = (scopeKey: string, rootState: any): string => {
  const user = rootState && rootState.authen ? rootState.authen.user : null
  if (user && user.user_type === 'leadership' && user.team) {
    return user.team
  }
  return scopeKey
}

const collectTeamIds = (scopeKey: string, organization: any): string[] => {
  const selected: { [key: string]: boolean } = {}
  const queue: string[] = [scopeKey]
  while (queue.length > 0) {
    const teamId = queue.shift()
    if (!teamId || selected[teamId]) {
      continue
    }
    selected[teamId] = true
    const children = organization && organization[teamId] ? Object.keys(organization[teamId]) : []
    children.forEach((childId: string) => {
      if (!selected[childId]) {
        queue.push(childId)
      }
    })
  }
  return Object.keys(selected)
}

const buildAllowedEmailSet = (scopeKey: string, rootState: any): Set<string> | null => {
  const user = rootState && rootState.authen ? rootState.authen.user : null
  if (user && user.user_type === 'leadership' && !user.team) {
    return new Set<string>()
  }
  const scopedScopeKey = getLeadershipScopedScopeKey(scopeKey, rootState)
  const scopeType: 'all' | 'team' | 'cohort' = user && user.user_type === 'leadership' && user.team
    ? 'team'
    : getScopeType(scopedScopeKey, rootState)
  if (scopeType === 'all') {
    return null
  }

  const allowed = new Set<string>()
  if (scopeType === 'cohort') {
    const cohortUsers = rootState && rootState.leaderboards && rootState.leaderboards.cohortUser && rootState.leaderboards.cohortUser[scopedScopeKey]
      ? rootState.leaderboards.cohortUser[scopedScopeKey]
      : []
    cohortUsers.forEach((entry: any) => {
      if (typeof entry === 'string') {
        const email = normalizeEmail(entry)
        if (email) {
          allowed.add(email)
        }
      } else if (entry && entry.email) {
        const email = normalizeEmail(entry.email)
        if (email) {
          allowed.add(email)
        }
      }
    })
    return allowed
  }

  const teamIds = collectTeamIds(scopedScopeKey, rootState && rootState.main && rootState.main.organization ? rootState.main.organization : {})
  const allUser = rootState && rootState.main && rootState.main.allUser ? rootState.main.allUser : {}
  for (const key in allUser) {
    const user = allUser[key]
    if (!user || !user.team || !teamIds.includes(user.team)) {
      continue
    }
    const email = user.email ? normalizeEmail(user.email) : normalizeEmail(decodeEmailKey(key))
    if (email) {
      allowed.add(email)
    }
  }
  return allowed
}

const extractRecordEmails = (record: any): string[] => {
  const emails: string[] = []
  const addEmail = (email: any) => {
    const normalized = normalizeEmail(email)
    if (normalized) {
      emails.push(normalized)
    }
  }

  addEmail(record && record.sender_email ? record.sender_email : '')
  if (record && typeof record.send_to_email === 'string') {
    record.send_to_email.split(',').forEach((email: string) => addEmail(email))
  } else if (record && Array.isArray(record.send_to_email)) {
    record.send_to_email.forEach((email: string) => addEmail(email))
  }
  if (record && record.profileimage && typeof record.profileimage === 'object') {
    Object.keys(record.profileimage).forEach((key) => {
      addEmail(key.includes('||') ? decodeEmailKey(key) : key)
    })
  }

  return _.uniq(emails)
}

const filterRecordsByScope = (records: Array<any>, allowedEmailSet: Set<string> | null): Array<any> => {
  if (!allowedEmailSet) {
    return records
  }
  if (allowedEmailSet.size === 0) {
    return []
  }
  return records.filter((record: any) => {
    const recordEmails = extractRecordEmails(record)
    return recordEmails.some((email) => allowedEmailSet.has(email))
  })
}

const filterUsersByScope = (users: Array<any>, allowedEmailSet: Set<string> | null): Array<any> => {
  if (!Array.isArray(users)) {
    return []
  }
  if (!allowedEmailSet) {
    return users || []
  }
  if (allowedEmailSet.size === 0) {
    return []
  }
  return users.filter((entry: any) => {
    if (typeof entry === 'string') {
      return allowedEmailSet.has(normalizeEmail(entry))
    }
    if (entry && entry.email) {
      return allowedEmailSet.has(normalizeEmail(entry.email))
    }
    return false
  })
}

const filterRecordsByDayRange = (records: Array<any>, startDay: string, endDay: string): Array<any> => {
  return (records || []).filter((record: any) => {
    const dayKey = toDayKey(record && record.date ? record.date : '')
    return !!dayKey && dayKey >= startDay && dayKey <= endDay
  })
}

const getActivityCompanyId = (value: any, rootState: any): string => {
  if (value && value.company) {
    return value.company
  }
  if (value && value.user && value.user.company) {
    return value.user.company
  }
  return rootState && rootState.authen && rootState.authen.user
    ? rootState.authen.user.company
    : ''
}

const getActivityCompanyName = (value: any, rootState: any): string => {
  if (value && value.user && value.user.company_name) {
    return value.user.company_name
  }
  return rootState && rootState.authen && rootState.authen.user && rootState.authen.user.company_name
    ? rootState.authen.user.company_name
    : ''
}

const buildMedalOptions = (companyData: any): Array<any> => {
  const medal = [
    { type: '+3 coins' },
    { type: '+5 coins' },
    { type: '+1 gems' },
  ]

  if (companyData && companyData.medal) {
    for (const key in companyData.medal) {
      medal.push({ type: companyData.medal[key] })
    }
  }

  medal.push(
    { type: 'All coins' },
    { type: 'All gems' },
  )

  return medal
}

const buildClassroomsGiftLookup = (classroomsGift: Array<any>): { [key: string]: Array<any> } => {
  const lookup: { [key: string]: Array<any> } = {}
  ;(classroomsGift || []).forEach((gift: any) => {
    if (!gift || !gift.class_id) {
      return
    }
    if (!lookup[gift.class_id]) {
      lookup[gift.class_id] = []
    }
    lookup[gift.class_id].push(gift)
  })
  return lookup
}

const resolveRewardLabel = (rewardValue: any): string => {
  if (Array.isArray(rewardValue)) {
    return rewardValue.join(',')
  }
  return rewardValue || ''
}

const formatActivitySenderName = (record: any, companyName: string): string => {
  if (!record || !record.send_anony_name) {
    return companyName
  }
  const senderParts = `${record.send_anony_name}`.split(' ')
  const firstName = senderParts[0] || ''
  const lastInitial = senderParts[1] ? senderParts[1].slice(0, 1) : ''
  return lastInitial ? `${firstName} ${lastInitial}` : firstName
}

const formatActivityRecipientName = (record: any): string => {
  if (!record) {
    return ''
  }
  if (record.send_to_name === 'Everyone') {
    return 'All Members'
  }
  if (_.size(record.profileimage) > 1) {
    return `Multiple (${_.size(record.profileimage)})`
  }
  if (record.send_to_name === 'Avenger') {
    return 'Avenger'
  }
  const sendParts = record.send_to_name ? `${record.send_to_name}`.split(' ') : []
  const firstName = sendParts[0] || ''
  const lastInitial = sendParts[1] ? sendParts[1].slice(0, 1) : ''
  return lastInitial ? `${firstName} ${lastInitial}` : firstName
}

const normalizeCoreValueAi = (record: any): Array<any> => {
  if (!record || !Array.isArray(record.core_value_ai)) {
    return []
  }
  return record.core_value_ai.filter((item: any) => item && item.rating > 4)
}

const normalizeRecognitionRow = (
  sourceRecord: any,
  companyName: string,
  classroomsGiftLookup: { [key: string]: Array<any> }
): any => {
  const record: any = {
    ...sourceRecord,
    from: formatActivitySenderName(sourceRecord, companyName),
    send_to: formatActivityRecipientName(sourceRecord),
    core_value_ai: normalizeCoreValueAi(sourceRecord),
  }

  if (record.date) {
    record.send_on = `${format(record.date, 'MMM D, YYYY')} at ${format(record.date, 'h:mmA')}`
  }

  if (record.type === 'coin' && record.medal !== undefined) {
    record.reward = resolveRewardLabel(record.medal)
    record.rewardSorting = '1'
    return record
  }

  if (record.type === 'classroom gift') {
    const gifts = classroomsGiftLookup[record.class_id2] || []
    if (gifts.length > 1) {
      let count = 0
      const emails: Array<string> = []
      gifts.forEach((gift: any) => {
        const members = Array.isArray(gift && gift.members) ? gift.members : []
        count += members.length
        members.forEach((member: any) => {
          if (member && member.email) {
            emails.push(member.email)
          }
        })
      })
      record.send_to = `Multiple (${count})`
      if (emails.length > 0) {
        record.send_to_email = emails.join(',')
      }
    } else {
      record.send_to = ''
    }
    record.from = companyName
    record.reason = `Attending ${record.reason} in the Happily University`
    record.unit = record.point > 1 ? 'coins' : 'coin'
    record.rewardSorting = `3_${record.point}`
    return record
  }

  if (record.type === 'gem' && record.category === undefined) {
    record.unit = record.point > 1 ? 'gems' : 'gem'
    record.rewardSorting = `2_${record.point}`
    return record
  }

  if (record.type === 'powerup') {
    record.from = companyName
    if (record.type2 === 'coin') {
      record.unit = record.point > 1 ? 'coins' : 'coin'
      record.rewardSorting = `3_${record.point}`
    } else if (record.type2 === 'gem') {
      record.unit = record.point > 1 ? 'gems' : 'gem'
      record.rewardSorting = `2_${record.point}`
    } else if (record.type2 === 'medal') {
      record.reward = record.medals && record.medal && record.medals[record.medal]
        ? record.medals[record.medal].en
        : resolveRewardLabel(record.medal)
      record.rewardSorting = '1'
    }
    return record
  }

  if (record.type === 'coin' && record.category === undefined) {
    record.unit = record.point > 1 ? 'coins' : 'coin'
    record.rewardSorting = `3_${record.point}`
    return record
  }

  return null
}

const normalizeRecognitionRows = (
  records: Array<any>,
  companyName: string,
  classroomsGiftLookup: { [key: string]: Array<any> }
): Array<any> => {
  return (records || [])
    .map((record: any) => normalizeRecognitionRow(record, companyName, classroomsGiftLookup))
    .filter((record: any) => !!record)
}

const getActivityHashtagNames = (record: any): Array<string> => {
  if (record && Array.isArray(record.hashtags) && record.hashtags.length > 0) {
    return record.hashtags
      .map((item: any) => item && item.name ? item.name : item)
      .filter((item: any) => !!item)
  }
  if (record && Array.isArray(record.hashtag)) {
    return record.hashtag.filter((item: any) => !!item)
  }
  return []
}

const matchesActivityRewardType = (record: any, rewardType: string): boolean => {
  if (!record || !rewardType) {
    return false
  }
  if (record.type === 'coin' && record.medal !== undefined) {
    const medalValues = Array.isArray(record.medal) ? record.medal : [record.medal]
    return medalValues.includes(rewardType)
  }
  if (record.type === 'classroom gift') {
    return rewardType === 'All coins'
      || (rewardType === '+3 coins' && record.point === 3)
      || (rewardType === '+5 coins' && record.point === 5)
  }
  if (record.type === 'gem' && record.category === undefined) {
    return rewardType === 'All gems'
      || (rewardType === '+1 gems' && record.point === 1)
  }
  if (record.type === 'powerup') {
    if (record.type2 === 'coin') {
      return rewardType === 'All coins'
        || (rewardType === '+3 coins' && record.point === 3)
        || (rewardType === '+5 coins' && record.point === 5)
    }
    if (record.type2 === 'gem') {
      return rewardType === 'All gems'
        || (rewardType === '+1 gems' && record.point === 1)
    }
    if (record.type2 === 'medal') {
      const medalValues = Array.isArray(record.medal) ? record.medal : [record.medal]
      return medalValues.includes(rewardType)
    }
  }
  if (record.type === 'coin' && record.category === undefined) {
    return rewardType === 'All coins'
      || (rewardType === '+3 coins' && record.point === 3)
      || (rewardType === '+5 coins' && record.point === 5)
  }
  return false
}

const applyActivityFilters = (records: Array<any>, filters: any): Array<any> => {
  const coreValues = Array.isArray(filters && filters.core_value) ? filters.core_value : []
  const classifications = Array.isArray(filters && filters.classification) ? filters.classification : []
  const rewardTypes = Array.isArray(filters && filters.reward) ? filters.reward : []

  if (coreValues.length === 0 && classifications.length === 0 && rewardTypes.length === 0) {
    return records || []
  }

  return (records || []).filter((record: any) => {
    const passesCoreValue = coreValues.length === 0
      || getActivityHashtagNames(record).some((hashtagName) => coreValues.includes(hashtagName))
    const passesClassification = classifications.length === 0 || record.effort >= 100
    const passesReward = rewardTypes.length === 0
      || rewardTypes.some((rewardType) => matchesActivityRewardType(record, rewardType))

    return passesCoreValue && passesClassification && passesReward
  })
}

const buildActivityContext = (company: string, scopeKey: string, startAt: string, endAt: string): any => ({
  company,
  scopeKey,
  startAt,
  endAt,
})

const matchesActivityContext = (context: any, value: any, rootState: any): boolean => {
  if (!context) {
    return false
  }
  const company = getActivityCompanyId(value, rootState)
  const scopeKey = getLeadershipScopedScopeKey(value && value.scopeKey ? value.scopeKey : ALL_TEAMS_AND_COHORTS, rootState)
  const { startAt, endAt } = resolveDateRange(value)

  return context.company === company
    && context.scopeKey === scopeKey
    && context.startAt === startAt
    && context.endAt === endAt
}

const buildPeriodMetricsData = (value: any, scopedRecords: Array<any>, activeSummaryRecords: Array<any>, givenSummaryRecords: Array<any>, allowedEmailSet: Set<string> | null): any => {
  const currentStart = toDayKey(value && value.startDate ? value.startDate : '')
  const currentEnd = toDayKey(value && value.endDate ? value.endDate : '')
  const data:any = createEmptyOverviewData()

  if (!currentStart || !currentEnd) {
    return data
  }

  const totalDays = getInclusiveDayCount(currentStart, currentEnd)
  const comparisonEnd = addDaysToKey(currentStart, -1)
  const comparisonStart = addDaysToKey(comparisonEnd, -(totalDays - 1))
  const recognitionRecords = filterRecordsByDayRange(scopedRecords, comparisonStart, currentEnd)
  const recognitionByDay = buildRecognitionDailyTotals(recognitionRecords)
  const activeUsersByDay = buildSummaryUserMap(activeSummaryRecords, allowedEmailSet)
  const givenUsersByDay = buildSummaryUserMap(givenSummaryRecords, allowedEmailSet)
  const currentDays = buildDayKeyRange(currentStart, currentEnd)
  const previousDays = buildDayKeyRange(comparisonStart, comparisonEnd)

  currentDays.forEach((dayKey) => {
    const dayData = recognitionByDay[dayKey]
    if (!dayData) {
      return
    }
    data.totalRecognition += dayData.activity
    data.value_aligned += dayData.hashtag_value
  })

  previousDays.forEach((dayKey) => {
    const dayData = recognitionByDay[dayKey]
    if (!dayData) {
      return
    }
    data.pasttotalRecognition += dayData.activity
    data.pastvalue_aligned += dayData.hashtag_value
  })

  const currentParticipation = calculatePercentage(
    countUniqueUsers(collectUsersForDays(givenUsersByDay, currentDays)),
    countUniqueUsers(collectUsersForDays(activeUsersByDay, currentDays)),
    0
  )
  const previousParticipation = calculatePercentage(
    countUniqueUsers(collectUsersForDays(givenUsersByDay, previousDays)),
    countUniqueUsers(collectUsersForDays(activeUsersByDay, previousDays)),
    0
  )
  const currentPercentValueAligned = calculatePercentage(data.value_aligned, data.totalRecognition, 0)
  const previousPercentValueAligned = data.pasttotalRecognition > 0
    ? (data.pastvalue_aligned * 100) / data.pasttotalRecognition
    : 0

  data.participation = currentParticipation
  data.past_participation = (parseFloat(currentParticipation) - parseFloat(previousParticipation)).toFixed(0)
  data.compareRecognition = calculateChange(data.totalRecognition, data.pasttotalRecognition)
  data.comparevalue_aligned = calculateChange(data.value_aligned, data.pastvalue_aligned)
  data.percent_value_aligned = currentPercentValueAligned
  data.comparepercent_value_aligned = Number.isFinite(parseFloat(currentPercentValueAligned) - previousPercentValueAligned)
    ? (parseFloat(currentPercentValueAligned) - previousPercentValueAligned).toFixed(0)
    : '0'

  buildMetricBuckets(value.time, currentStart, currentEnd).forEach((bucket) => {
    let bucketRecognition = 0
    let bucketValueAligned = 0
    bucket.days.forEach((dayKey) => {
      const dayData = recognitionByDay[dayKey]
      if (!dayData) {
        return
      }
      bucketRecognition += dayData.activity
      bucketValueAligned += dayData.hashtag_value
    })

    data.labels.push(bucket.label)
    data.chartRecognition.push(bucketRecognition)
    data.chartvalue_aligned.push(bucketValueAligned)
    data.chartpercent_value_aligned.push(calculatePercentage(bucketValueAligned, bucketRecognition, 0))
    data.chartparticipation.push(calculatePercentage(
      countUniqueUsers(collectUsersForDays(givenUsersByDay, bucket.days)),
      countUniqueUsers(collectUsersForDays(activeUsersByDay, bucket.days)),
      0
    ))
  })

  return data
}
export default {
    namespaced: true,
    state: {
      activity:[],
      activityall:[],
      durationActivityall:[],
      page:1,
      size_page:10,
      results: 0,
      list_month:[],
      reportActivity:[],
      activityAllfilter:[],
      filter:false,
      barchart:[],
      radarchart:{radarchart:[{hashtag:'',value:''}],label:[]},
      chartLoading:true,
      metricsLoading:true,
      listChartmonth:[],
      radarchartAll:[],
      participation:[],
      medal:[],
      dataOverAll:{          
        totalRecognition:0,
        pasttotalRecognition:0,
        chartRecognition:[],
        compareRecognition:'',
        value_aligned:0,
        pastvalue_aligned:0,
        comparevalue_aligned:'',
        percent_value_aligned:'',
        comparepercent_value_aligned:'',

        participation:''
        },
      data_all_day:[],
      data_all_day_participation:[],
      herepoint_cache:[] as Array<any>,
      herepoint_cache_company:'',
      activity_context:null as any,
    },
    mutations: {
      setActivity(state:any, result:any) {
        
        state.activity = result
      },
      setActivityall(state:any, result:any) {
        state.activityall = result
      },
      setDurationActivityall(state:any, result:any) {
        state.durationActivityall = result
      },
      setPage(state:any,result:any) {
        state.page = result
      },
      setResults(state:any, result:any) {
        state.results = result
      },
      setMedal(state:any, result:any) {
        state.medal = result
      },
      setList_month(state:any, result:any) {
        state.list_month = result
      },
      setActivityReport(state:any, result:any) {
        state.reportActivity = result
      },
      setActivityAllfilter(state:any, result:any) {
        state.activityAllfilter = result
      },
      setFilter(state:any, result:any) {
        state.filter = result
      },
      setBarchart(state:any, result:any) {
        
        state.barchart = result
      },
      setChartLoading(state:any, result:any) {
        state.chartLoading = result
      },
      setMetricsLoading(state:any, result:any) {
        state.metricsLoading = result
      },
      setRadarchart(state:any, result:any) {
        state.radarchart = result
      },
      setRadarchartAll(state:any, result:any) {
        state.radarchartAll = result
      },
      setParticipation(state:any, result:any) {
        state.participation = result
      },
      setListChartmonth(state:any, result:any) {
        state.listChartmonth = result
      },
      setDataOverAll(state:any, result:any) {
        state.dataOverAll = result
      },
      setdata_all_day(state:any, result:any) {
        state.data_all_day = result
      },
      setdata_all_day_participation(state:any, result:any) {
        state.data_all_day_participation = result
      },
      setHerepointCache(state:any, result:any) {
        state.herepoint_cache = result
      },
      setHerepointCacheCompany(state:any, result:any) {
        state.herepoint_cache_company = result
      },
      setActivityContext(state:any, result:any) {
        state.activity_context = result
      }
    },
    actions: {
      invalidateDurationCaches(context:any, value:any){
        const company = value && value.company
          ? value.company
          : value && value.user && value.user.company
            ? value.user.company
            : ''
        if (company) {
          clearCompanyDurationCaches(company)
        }
      },
      async buildTablePayloadData({rootState}:any, value:any){
        const company = getActivityCompanyId(value, rootState)
        const companyName = getActivityCompanyName(value, rootState)
        const scopeKey = getLeadershipScopedScopeKey(value && value.scopeKey ? value.scopeKey : ALL_TEAMS_AND_COHORTS, rootState)
        const { startAt, endAt } = resolveDateRange(value)
        const startDay = startAt.slice(0, 10)
        const endDay = endAt.slice(0, 10)

        if (!company || !startDay || !endDay) {
          return {
            medal: [],
            tableRows: [],
            results: 0,
            context: buildActivityContext(company, scopeKey, startAt, endAt),
          }
        }

        const allowedEmailSet = buildAllowedEmailSet(scopeKey, rootState)
        const [companyData, classroomsGift, rawHerepoint] = await Promise.all([
          getCompany(company),
          fetchClassroomsGiftCached(company),
          fetchDurationRecognitionRange(company, startDay, endDay),
        ])

        const classroomsGiftLookup = buildClassroomsGiftLookup(classroomsGift)
        const scopedHerepoint = filterRecordsByDayRange(
          filterRecordsByScope(rawHerepoint, allowedEmailSet),
          startDay,
          endDay
        )
        const tableRows = normalizeRecognitionRows(scopedHerepoint, companyName, classroomsGiftLookup)

        return {
          medal: buildMedalOptions(companyData),
          tableRows,
          results: tableRows.length,
          context: buildActivityContext(company, scopeKey, startAt, endAt),
        }
      },
      async buildPeriodPayloadData({rootState}:any, value:any){
        const company = getActivityCompanyId(value, rootState)
        const companyName = getActivityCompanyName(value, rootState)
        const scopeKey = getLeadershipScopedScopeKey(value && value.scopeKey ? value.scopeKey : ALL_TEAMS_AND_COHORTS, rootState)
        const currentStart = toDayKey(value && value.startDate ? value.startDate : '')
        const currentEnd = toDayKey(value && value.endDate ? value.endDate : '')
        const includeTableRows = !(value && value.includeTableRows === false)
        const currentStartAt = currentStart ? `${currentStart} 00:00` : ''
        const currentEndAt = currentEnd ? `${currentEnd} 23:59` : ''

        if (!company || !currentStart || !currentEnd) {
          return {
            medal: [],
            tableRows: [],
            results: 0,
            metrics: createEmptyOverviewData(),
            context: buildActivityContext(company, scopeKey, currentStartAt, currentEndAt),
          }
        }

        const totalDays = getInclusiveDayCount(currentStart, currentEnd)
        const comparisonEnd = addDaysToKey(currentStart, -1)
        const comparisonStart = addDaysToKey(comparisonEnd, -(totalDays - 1))
        const allowedEmailSet = buildAllowedEmailSet(scopeKey, rootState)

        const [companyData, classroomsGift, rawHerepoint, activeSummaryRecords, givenSummaryRecords] = await Promise.all([
          getCompany(company),
          includeTableRows ? fetchClassroomsGiftCached(company) : Promise.resolve([]),
          fetchDurationRecognitionRange(company, comparisonStart, currentEnd),
          fetchDurationSummaryRange(company, 'summary_active_users', comparisonStart, currentEnd),
          fetchDurationSummaryRange(company, 'summary_send_coins', comparisonStart, currentEnd),
        ])

        const scopedRecords = filterRecordsByDayRange(
          filterRecordsByScope(rawHerepoint, allowedEmailSet),
          comparisonStart,
          currentEnd
        )
        const metrics = buildPeriodMetricsData(
          value,
          scopedRecords,
          activeSummaryRecords,
          givenSummaryRecords,
          allowedEmailSet
        )
        const tableRows = includeTableRows
          ? normalizeRecognitionRows(
            filterRecordsByDayRange(scopedRecords, currentStart, currentEnd),
            companyName,
            buildClassroomsGiftLookup(classroomsGift)
          )
          : []

        return {
          medal: buildMedalOptions(companyData),
          tableRows,
          results: tableRows.length,
          metrics,
          context: buildActivityContext(company, scopeKey, currentStartAt, currentEndAt),
        }
      },
      async loadPeriodPayload({commit,dispatch,state}:any, value:any){
        commit('setMetricsLoading', true)
        try {
          const payload = await dispatch('buildPeriodPayloadData', value)
          commit('setDataOverAll', payload.metrics)
          commit('setMedal', payload.medal)
          commit('setResults', payload.results)
          commit('setDurationActivityall', payload.tableRows)
          commit('setActivityall', payload.tableRows)
          commit('setActivity', payload.tableRows.slice(0, state.size_page))
          commit('setPage', 1)
          commit('setActivityContext', payload.context)
          return payload
        } finally {
          commit('setMetricsLoading', false)
        }
      },
      async getData({commit,rootState,dispatch,state}:any, value:any){
        const payload = await dispatch('buildTablePayloadData', value)
        commit('setMedal', payload.medal)
        commit('setResults', payload.results)
        commit('setActivityall', payload.tableRows)
        commit('setActivity', payload.tableRows.slice(0, state.size_page))
        commit('setPage', 1)
        commit('setActivityContext', payload.context)
        return payload.tableRows
      },
      currentChange({commit,state}:any,page:any){
        var size = state.size_page
        var start_row = (page-1) * size
        var end_row = ((page-1) * size)+size
 
       
        let data: Array<any> = [];
        if(state.filter==true){
          data.push(state.activityAllfilter)
        }else{
          data.push(state.activityall)
        }

        commit('setPage',page)
        commit('setActivity',data[0].slice(start_row,end_row))
      },
      deletePowerup({commit,state,rootState,dispatch}:any,value:any){
        if(value.id){
          firebase.firestore().collection('powerups').doc(value.id).delete().then(function() {
            dispatch('getData',rootState.authen.user)
          });
        }
      },
      getList_month({ commit, dispatch, state }: any, user: any) {
        const arr_list_month: Array<any> = [];
        const arr_chart_month: Array<any> = [];
        var dateNow = new Date()
        for(let i=0;i<=11;i++){
          arr_list_month.push({'value':format(addMonths(dateNow, -i), 'YYYY-MM'),'name':format(addMonths(dateNow, -i), 'MMMM YYYY'),'id':format(addMonths(dateNow, -i), 'MMMM YYYY')})
        }
        for(let i=11;i>=0;i--){
          arr_chart_month.push(format(subMonths(new Date(), i), 'MMM YYYY'))
        }
        commit('setList_month', arr_list_month)
        commit('setListChartmonth', arr_chart_month)
      },
      async fetchHerepointRangeChunked(context:any, value:any) {
        const company = value && value.company ? value.company : ''
        const startDay = toDayKey(value && value.startDate ? value.startDate : '')
        const endDay = toDayKey(value && value.endDate ? value.endDate : '')
        if (!company || !startDay || !endDay) {
          return []
        }

        const allRecords:Array<any> = []
        const snapshots = await Promise.all(
          buildMonthlyQueryChunks(startDay, endDay).map((chunk) =>
            firebase.firestore().collection('here_point')
              .where('company', '==', company)
              .where('statusrow', '==', 'sendcoin')
              .where('date', '>=', chunk.start)
              .where('date', '<=', chunk.end)
              .get()
          )
        )

        snapshots.forEach((snapshot) => {
          snapshot.forEach((doc:any) => {
            const data = doc.data()
            data.id = doc.id
            allRecords.push(data)
          })
        })

        return allRecords
      },
      async fetchMonthlyParticipation({commit,rootState}:any, value:any){
        const scopeKey = getLeadershipScopedScopeKey(value && value.scopeKey ? value.scopeKey : ALL_TEAMS_AND_COHORTS, rootState)
        const allowedEmailSet = buildAllowedEmailSet(scopeKey, rootState)
        const monthKeys = buildLastTwelveMonthKeys()
        const startDay = getMonthStartKey(monthKeys[0])
        const endDay = toDayKey(new Date())
        const activeUsersByMonth:any = {}
        const givenUsersByMonth:any = {}

        monthKeys.forEach((monthKey) => {
          activeUsersByMonth[monthKey] = []
          givenUsersByMonth[monthKey] = []
        })

        const [activeRecords, givenRecords] = await Promise.all([
          fetchDurationSummaryRange(value.company, 'summary_active_users', startDay, endDay),
          fetchDurationSummaryRange(value.company, 'summary_send_coins', startDay, endDay),
        ])

        activeRecords.forEach((data:any) => {
          const monthKey = toMonthKey(data && data.date ? data.date : '')
          if (!monthKey || !activeUsersByMonth[monthKey]) {
            return
          }
          const sourceUsers = Array.isArray(data && data.users) ? data.users : []
          const scopedUsers = filterUsersByScope(sourceUsers, allowedEmailSet)
          activeUsersByMonth[monthKey].push(...scopedUsers)
        })

        givenRecords.forEach((data:any) => {
          const monthKey = toMonthKey(data && data.date ? data.date : '')
          if (!monthKey || !givenUsersByMonth[monthKey]) {
            return
          }
          const sourceUsers = Array.isArray(data && data.users) ? data.users : []
          const scopedUsers = filterUsersByScope(sourceUsers, allowedEmailSet)
          givenUsersByMonth[monthKey].push(...scopedUsers)
        })

        const rate = monthKeys.map((monthKey) => {
          return Number(calculatePercentage(
            countUniqueUsers(givenUsersByMonth[monthKey]),
            countUniqueUsers(activeUsersByMonth[monthKey]),
            2
          ))
        })

        commit('setParticipation',rate)
        return rate
      },
      async getMonthlyTrend({commit,dispatch}:any, value:any){
        commit('setChartLoading',true)
        await Promise.all([
          dispatch('fetchMonthlyParticipation', value),
          dispatch('fetchHerepointChunked', value.company),
        ])
        await dispatch('recomputeChartFromCache', value)
      },
      async getPeriodMetrics({commit,rootState,dispatch}:any, value:any){
        commit('setMetricsLoading', true)
        try {
          const includeTableRows = !!(value && value.includeTableRows)
          const payload = await dispatch('buildPeriodPayloadData', {
            ...value,
            includeTableRows,
          })
          commit('setDataOverAll', payload.metrics)
          if (includeTableRows) {
            commit('setDurationActivityall', payload.tableRows)
          }
          return payload.metrics
        } finally {
          commit('setMetricsLoading', false)
        }
      },
      async getDataReport({commit,rootState,dispatch,state}:any, value:any){
        let reportRows:Array<any> = []

        if (matchesActivityContext(state.activity_context, value, rootState)) {
          reportRows = state.activityall || []
        } else {
          const payload = await dispatch('buildTablePayloadData', value)
          reportRows = payload.tableRows
        }

        commit('setActivityReport', applyActivityFilters(reportRows, value))
      },
      getdatafilter({commit,rootState,dispatch,state}:any, value:any){
        let filteredRows:Array<any> = [];
        var start_row = (state.page-1) * state.size_page
        var end_row = ((state.page-1) * state.size_page)+state.size_page
        filteredRows = applyActivityFilters(state.activityall, value)

        if(value.reward.length==0&&value.classification.length==0&&value.core_value.length==0){
          commit('setFilter',false)
        } else {
          commit('setFilter',true)
        }

        commit('setResults',filteredRows.length)
        commit('setActivity', filteredRows.slice(start_row,end_row))
        commit('setActivityAllfilter', filteredRows)
      },
      sortData({commit,state}:any,val:any){
        var size = state.size_page
        var start_row = (state.page-1) * size
        var end_row = ((state.page-1) * size)+size
        let data: Array<any> = [];
        const order=val.order=='ascending'? 'asc':'desc'
        if(state.filter==true){
          if(val.column=='date'){
            data=_.orderBy(state.activityAllfilter, function(dateObj) {
              return new Date(dateObj.date);
            },[order]);
          }else{
            data=_.orderBy(state.activityAllfilter, [val.column],[order]);
          }
          
          commit('setActivityAllfilter', data)
        }else{
          if(val.column=='date'){
            data=_.orderBy(state.activityall, function(dateObj) {
  
              return new Date(dateObj.date);
            },[order]);
      
          }else{
            data=_.orderBy(state.activityall, [val.column], [order]);
          }
          commit('setActivityall', data)
        }
        commit('setActivity',data.slice(start_row,end_row)) 
       
      },
      async fetchHerepointChunked({commit}:any, company:string) {
        if (herepointChunkRequests[company]) {
          return herepointChunkRequests[company]
        }

        herepointChunkRequests[company] = (async () => {
          const monthKeys = buildLastTwelveMonthKeys()
          const startDay = getMonthStartKey(monthKeys[0])
          const endDay = toDayKey(new Date())
          const allRecords = await fetchDurationRecognitionRange(company, startDay, endDay)

          commit('setHerepointCache', allRecords)
          commit('setHerepointCacheCompany', company)
          return allRecords
        })()

        try {
          return await herepointChunkRequests[company]
        } finally {
          delete herepointChunkRequests[company]
        }
      },
      async getChart({commit,dispatch}:any, value:any){
        commit('setChartLoading',true)
        await dispatch('fetchHerepointChunked', value.company)
        await dispatch('recomputeChartFromCache', value)


        // await firebase.firestore().collection('here_point').where('company','==',value.company).where('statusrow','in',['sendcoin']).where('date','>=',startAt).get()
        // .then((querySnapshot)=> {
        //     querySnapshot.forEach((doc)=> {
        //       const u = doc.data()
        //       u.id = doc.id
        //       if(doc.data().statusrow){
        //         if(!data_day[format(u.date, 'YYYY-MM-DD')]){
        //           data_day[format(u.date, 'YYYY-MM-DD')]={
        //             activity:0,
        //             hashtag_value:0,
        //             hashtag:{},
        //             date:format(u.date, 'YYYY-MM-DD')}
        //           }
                
        //         data_day[format(u.date, 'YYYY-MM-DD')].activity++
        //         let date=format(u.date, 'YYYY-MM')
        //         if(!arrData[date]){
        //           arrData[date]={
        //             ...obj,
        //             activity:0,
        //             hashtag_value:0
        //           }
        //         }
        //         arrData[date].activity++
        //         if(u.hashtag&&u.hashtag.length>0){
        //           data_day[format(u.date, 'YYYY-MM-DD')].hashtag_value++
        //           arrData[date].hashtag_value++
        //           u.hashtag.map(v=>{
        //             let hashtags=v.replace("#","")
        //             if(!data_day[format(u.date, 'YYYY-MM-DD')].hashtag[hashtags]){
        //               data_day[format(u.date, 'YYYY-MM-DD')].hashtag[hashtags]=0
        //             }
        //             data_day[format(u.date, 'YYYY-MM-DD')].hashtag[hashtags]++
        //             arrData[date][hashtags]++
        //           })
        //         }
        //       }
        //     });
        //     for(var i=11;i>=0;i--){
        //       const radar:Array<any> = [];
        //       let d= format(subMonths(new Date(), i), 'YYYY-MM')
        //       if(arrData[d]!==undefined){
        //         hashtagBarchart.push(arrData[d].hashtag_value)
        //         barchart.push(arrData[d].activity)
        //         if(arrData[d].hashtag){
                  
        //           for(const i in arrData[d].hashtag){
        //           //  radar.push(i)
        //             radar.push({hashtag:i,value:arrData[d].hashtag[i]})
        //           }
                  
              
        //         }
        //         else{

        //           hashtag.map(data=>{
                    
        //             // console.log('arrData[d][data]',arrData[d][data])
        //              //radar.push(arrData[d][data])
        //             radar.push({hashtag:data,value:arrData[d][data]})
        //           })
        //         }
          
           
        //         radarchart[d]=radar
        //       }else{
        //        let _data ={
        //           activity: 0,
        //           alwaysdatadriven: 0,
        //           bepunctual: 0,
        //           brave: 0,
        //           empathy: 0,
        //           focus: 0,
        //           hashtag_value: 0,
        //           keepinsync: 0,
        //           opencommunication: 0,
        //           perfectdetails: 0,
        //           problemsolver: 0,
        //           teamwork: 0,
        //           usersrule: 0,
        //           }
        //         hashtagBarchart.push(_data.hashtag_value)
        //         barchart.push(_data.activity)
        //         hashtag.map(data=>{
        //            radar.push({hashtag:data,value:_data[data]})
        //           // radar.push(_data[data])
        //         })
        //         radarchart[d]=radar
        //       }             
        //     }

        //     for(const i in data_day){
        //       data_all_day.push(data_day[i])
        //     }
        //     commit('setdata_all_day',data_all_day)
           
        //     commit('setBarchart',{barchart:barchart,hashtagBarchart:hashtagBarchart,participation:state.participation})  
        //     commit('setRadarchart',{radarchart:radarchart[current],label:hashtag}) 
        //     commit('setRadarchartAll',radarchart) 
        //     dispatch('getOverAll','Past 30 days')
            
           
        // });

      },
      async recomputeChartFromCache({commit,rootState,dispatch,state}:any, value:any){
        commit('setChartLoading',true) 
        const scopeKey = getLeadershipScopedScopeKey(value && value.scopeKey ? value.scopeKey : ALL_TEAMS_AND_COHORTS, rootState)
        const allowedEmailSet = buildAllowedEmailSet(scopeKey, rootState)
        const arrData:any = {};
        const obj: { [key: string]: string} = {};
        const hashtag :Array<any> = [];
        const barchart:Array<any> = [];
        const radarchart:any = {};
        const hashtagBarchart:Array<any> = [];
        const data_all_day:Array<any> = [];
        var data_day:{ [key: string]: any} = {};
        const company = await getCompany(value.company)
        const timezone = await getCompanyTimezone(value.company)
        const companyNow = toCompanyDate(new Date(), timezone)
        var current = format(companyNow, 'YYYY-MM')
        if(company.hashtag){
          for(var data in company.hashtag){
            var hashtags=company.hashtag[data].name.replace("#","")
            var str=hashtags.toString()
            hashtag.push(str)
            Object.assign(obj,{[str]:0});
          }
        }

        var herepointRaw = state.herepoint_cache || []
        var herepoint = filterRecordsByScope(herepointRaw, allowedEmailSet)

        for(var hi = 0; hi < herepoint.length; hi++){
          var u:any = herepoint[hi]
          if(u.statusrow){
            var dayKey = format(u.date, 'YYYY-MM-DD')
            if(!data_day[dayKey]){
              data_day[dayKey]={
                activity:0,
                hashtag_value:0,
                hashtag:{},
                date:dayKey}
            }
            data_day[dayKey].activity++
            var monthKey = format(u.date, 'YYYY-MM')
            if(!arrData[monthKey]){
              arrData[monthKey]={
                ...obj,
                activity:0,
                hashtag_value:0,
                hashtag:{}
              }
            }
            arrData[monthKey].activity++
            if(u.hashtag&&u.hashtag.length>0){
              data_day[dayKey].hashtag_value++
              arrData[monthKey].hashtag_value++
              u.hashtag.map(function(v:any){
                var ht=v.replace("#","")
                if(!data_day[dayKey].hashtag[ht]){
                  data_day[dayKey].hashtag[ht]=0
                }
                data_day[dayKey].hashtag[ht]++
                if(!arrData[monthKey].hashtag){
                  arrData[monthKey].hashtag={}
                }
                if(!arrData[monthKey].hashtag[ht]){
                  arrData[monthKey].hashtag[ht]=0
                }
                arrData[monthKey].hashtag[ht]++
                if(arrData[monthKey][ht] === undefined){
                  arrData[monthKey][ht]=0
                }
                arrData[monthKey][ht]++
              })
            }
          }
        }

        for(var mi=11;mi>=0;mi--){
          var radar:Array<any> = [];
          var d= format(subMonths(companyNow, mi), 'YYYY-MM')
          if(arrData[d]!==undefined){
            hashtagBarchart.push(arrData[d].hashtag_value)
            barchart.push(arrData[d].activity)
            if(arrData[d].hashtag){
              for(var hk in arrData[d].hashtag){
                radar.push({hashtag:hk,value:arrData[d].hashtag[hk]})
              }
            } else {
              hashtag.map(function(htd:any){
                radar.push({hashtag:htd,value:arrData[d][htd]})
              })
            }
            radarchart[d]=radar
          } else {
            hashtagBarchart.push(0)
            barchart.push(0)
            hashtag.map(function(htd:any){
              radar.push({hashtag:htd,value:0})
            })
            radarchart[d]=radar
          }
        }

        for(var dk in data_day){
          data_all_day.push(data_day[dk])
        }

        commit('setdata_all_day',data_all_day)
        commit('setBarchart',{barchart:barchart,hashtagBarchart:hashtagBarchart,participation:state.participation})
        commit('setRadarchart',{radarchart:radarchart[current] || [],label:hashtag})
        commit('setRadarchartAll',radarchart)
        commit('setChartLoading',false) 
      },
      changeRedarChart({commit,rootState,dispatch,state}:any, value:any){
        let monthSelect = typeof value.selectedMonth === 'string' && /^\d{4}-\d{2}$/.test(value.selectedMonth)
          ? value.selectedMonth
          : format(value.selectedMonth, 'YYYY-MM')
        commit('setRadarchart',{radarchart:state.radarchartAll[monthSelect] || [],label:value.label})
      },
      async getRecognition({commit,rootState,dispatch,state}:any, value:any){
        commit('setChartLoading',true) 
        const scopeKey = getLeadershipScopedScopeKey(value && value.scopeKey ? value.scopeKey : ALL_TEAMS_AND_COHORTS, rootState)
        const allowedEmailSet = buildAllowedEmailSet(scopeKey, rootState)
        let activeUser:any={}
        let activeUserAll:any={}
        let givenAll:any={}
        let given:any={}
        const timezone = await getCompanyTimezone(value.company)
        const companyNow = toCompanyDate(new Date(), timezone)
        let currentDate=format(companyNow,"YYYY-MM")
        let month12ago=format(subMonths(companyNow, 11), 'YYYY-MM')
        let years2=format(subDays(companyNow, 730), 'YYYY-MM')
        let dateAll:Array<any>=[]
        let rate:Array<any>=[]
        for(let i=11;i>=0;i--){
          let d=format(subMonths(companyNow, i), 'YYYY-MM')
          dateAll.push(d)
          if(!activeUser[d]){
            activeUser[d]=[]
          }
          if(!given[d]){
            given[d]=[]
          }
        }
        // PG Read API: per-day active-user summary (replaces summary_active_users docs).
        pgSummary(years2, format(new Date(), 'YYYY-MM-DD'), value.company)
        .then((_sumDays:any)=> {
        (_sumDays||[]).forEach((doc:any)=> {
          let data=doc
          let date =format(data.date,"YYYY-MM")
          if(data.users){
            const scopedUsers = filterUsersByScope(data.users, allowedEmailSet)
            if(!activeUserAll[data.date]){
              activeUserAll[data.date]=[]
            }
            activeUserAll[data.date].push({date:data.date,users:scopedUsers})
            if(date>=month12ago&&date<=currentDate)
              {
                activeUser[date].push(...scopedUsers)
              }
          }
          
        })
        if(activeUser ){
          dateAll.map(data=>{
            activeUser[data]=_.groupBy(activeUser[data], function (b) { return b })
          })
        }
        firebase.firestore().collection('summary_send_coins').where("company", "==", rootState.authen.user.company).where('date', '>=', years2).get()
        .then((querySnapshot)=> {
        querySnapshot.forEach((doc)=> {
            let data=doc.data()
            let date =format(data.date,"YYYY-MM")
            const scopedUsers = filterUsersByScope(data.users, allowedEmailSet)
            if(!givenAll[data.date]){
              givenAll[data.date]=[]
            }
            givenAll[data.date].push({date:data.date,users:scopedUsers})
                if(date>=month12ago&&date<=currentDate)
                {
                  given[date].push(...scopedUsers)
                }
                
          })
            if(given){
              dateAll.map(data=>{
              
                given[data]=_.groupBy(given[data], function (b) { return b })
                rate.push( (Object.keys(given[data]).length*100/ Object.keys(activeUser[data]).length).toFixed(2))
              })
              commit('setdata_all_day_participation',{givenAll:givenAll,activeUserAll:activeUserAll})
              commit('setParticipation',rate)
              // dispatch('getChart',value)
            }

        })

      })

      },
      async getOverAll({commit,rootState,dispatch,state}:any, value:any){
        const timezone = await getCompanyTimezone(rootState.authen.user.company)
        const companyNow = toCompanyDate(new Date(), timezone)
        let activeUser:any=[]
        let givenUser:any=[]
        let past_activeUser:any=[]
        let past_givenUser:any=[]
        const data:any={
          totalRecognition:0,
          chartRecognition:[],
          labels:[],
          pasttotalRecognition:0,
          compareRecognition:'',
          value_aligned:0,
          pastvalue_aligned:0,
          comparevalue_aligned:'',
          chartvalue_aligned:[],
          percent_value_aligned:'',
          comparepercent_value_aligned:'',
          chartpercent_value_aligned:[],
          participation:'',
          chartparticipation:[],
          past_participation:''
        };
        let dataChart:any={
          totalRecognition:0,
          labels:[],
          value_aligned:0,
          percent_value_aligned:'',
          activeUser:[],
          givenUser:[],
          participation:'',
        };
        if(value=="Past 30 days"){
          for(let i=0;i<=60;i++){
            let date = format(addDays(companyNow,-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            let active_day=[]
            if(date>=format(addDays(companyNow,-30), 'YYYY-MM-DD')){
              if(state.data_all_day_participation.activeUserAll[date]){
                
                active_day=_.groupBy(state.data_all_day_participation.activeUserAll[date][0].users, function (b) { return b })
          
                //console.log('state.data_all_day_participation.activeUserAll[date]',state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
            }else{
              if(state.data_all_day_participation.activeUserAll[date]){
                //console.log('state.data_all_day_participation.activeUserAll[date]',state.data_all_day_participation.activeUserAll[date][0].users)
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
            }
            if(findData&&date>=format(addDays(companyNow,-30), 'YYYY-MM-DD')){
              
              let given_day=[]
              if(state.data_all_day_participation.givenAll[date]){
                 given_day=_.groupBy(state.data_all_day_participation.givenAll[date][0].users, function (b) { return b })
                 //console.log('state.data_all_day_participation.givenAll[date]',state.data_all_day_participation.givenAll[date][0].users)
                // data.givenUser.push(...state.data_all_day_participation.givenAll[date].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }

              const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
              data.chartparticipation.push(_rate_day)
              data.totalRecognition=data.totalRecognition+findData.activity
              data.chartRecognition.push(findData.activity)
              data.labels.push(date)
              data.value_aligned=data.value_aligned+findData.hashtag_value
              data.chartvalue_aligned.push(findData.hashtag_value)
              data.chartpercent_value_aligned.push(isNaN((findData.hashtag_value*100)/findData.activity)?'0':((findData.hashtag_value*100)/findData.activity).toFixed())
            }else if(findData){
              
              
              if(state.data_all_day_participation.givenAll[date]){
               // data.givenUser.push(...state.data_all_day_participation.givenAll[date].users)
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(i<=29){
              data.labels.push(date)
              data.chartRecognition.push('0')
              data.chartvalue_aligned.push('0')
              data.chartparticipation.push('0')
              data.chartpercent_value_aligned.push('0')
            
            }
          }
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
        }else if(value=="Past 90 days"){
          let first_week=''
          let _rate_day='0'
          for(let i=0;i<=180;i++){
            let date = format(addDays(companyNow,-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            const week = format(new Date(date), 'YYYY-W')
            if(first_week==''){
              first_week=week
            }
            if(date>=format(addDays(companyNow,-90), 'YYYY-MM-DD')){
              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
            }else{
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
            }
            
           
            if(findData&&date>=format(addDays(companyNow,-90), 'YYYY-MM-DD')){
              let active_day=[]
            let given_day=[]
              if(first_week!=week){
              active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
              given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
              _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
              data.chartvalue_aligned.push(dataChart.value_aligned)
              data.chartRecognition.push(dataChart.totalRecognition)
              data.chartparticipation.push(_rate_day)
              data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
              data.labels.push(first_week)
              first_week=week
              dataChart={
                totalRecognition:0,
                labels:[],
                value_aligned:0,
                percent_value_aligned:'',
                activeUser:[],
                givenUser:[],
                participation:'',
              };
              }
              
              
              if(state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }
              
              
              data.totalRecognition=data.totalRecognition+findData.activity
              data.value_aligned=data.value_aligned+findData.hashtag_value
              
              dataChart.totalRecognition=dataChart.totalRecognition+findData.activity
              dataChart.value_aligned=dataChart.value_aligned+findData.hashtag_value
               
              
            }else if(findData){
              // if(state.data_all_day_participation.activeUserAll[date]){
              //   //console.log('state.data_all_day_participation.activeUserAll[date]',state.data_all_day_participation.activeUserAll[date][0].users)
              //   past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              // }
              if(state.data_all_day_participation.givenAll[date]){
                //data.givenUser.push(...state.data_all_day_participation.givenAll[date].users)
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(i<90){
              if(first_week!=week){
       
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                _rate_day='0'
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
             
              }
            }
          }
          // const given=_.groupBy(givenUser, function (b) { return b })
          // const active=_.groupBy(activeUser, function (b) { return b })
          // const _given=_.groupBy(past_givenUser, function (b) { return b })
          // const _active=_.groupBy(past_activeUser, function (b) { return b })
          // const _rate= (Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          // const rate= (Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          // data.participation=rate
          // data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          // data.compareRecognition=(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          // data.comparevalue_aligned=(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          // data.percent_value_aligned=(((data.value_aligned*100)/data.totalRecognition).toFixed())
          // data.comparepercent_value_aligned=(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
        }else if(value=="Past 180 days"){

          let first_week=''
          let week=''
          for(let i=0;i<=360;i++){
            let date = format(addDays(companyNow,-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            const weekData = format(new Date(date), 'YYYY-W')
            if(first_week==''){
              first_week=format(addWeeks(new Date(date),-1), 'YYYY-W')
              week=format(new Date(date), 'YYYY-W')
            }
            let active_day=[]
            let given_day=[]
            if(date>=format(addDays(companyNow,-180), 'YYYY-MM-DD')){
              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
            }else{
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
            }




            if(findData&&date>=format(addDays(companyNow,-180), 'YYYY-MM-DD')){
              //bi-weekly
              if(first_week!=weekData&&week!=weekData){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(week)
                first_week=format(addWeeks(new Date(date),-1), 'YYYY-W')
                week=format(new Date(date), 'YYYY-W')
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }
              // if(state.data_all_day_participation.activeUserAll[date]){
                
              //   dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              //   activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              // }
              
              if(state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }
              
              
              data.totalRecognition=data.totalRecognition+findData.activity
              data.value_aligned=data.value_aligned+findData.hashtag_value
              
              dataChart.totalRecognition=dataChart.totalRecognition+findData.activity
              dataChart.value_aligned=dataChart.value_aligned+findData.hashtag_value
              
  
               

               
              
            }else if(findData){
              // if(state.data_all_day_participation.activeUserAll[date]){
              //   //console.log('state.data_all_day_participation.activeUserAll[date]',state.data_all_day_participation.activeUserAll[date][0].users)
              //   past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              // }
              if(state.data_all_day_participation.givenAll[date]){
                //data.givenUser.push(...state.data_all_day_participation.givenAll[date].users)
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(i<180){
              if(first_week!=weekData&&week!=weekData){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(week)
                first_week=format(addWeeks(new Date(date),-1), 'YYYY-W')
                week=format(new Date(date), 'YYYY-W')
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }
            }
          }
          // const given=_.groupBy(givenUser, function (b) { return b })
          // const active=_.groupBy(activeUser, function (b) { return b })
          // const _given=_.groupBy(past_givenUser, function (b) { return b })
          // const _active=_.groupBy(past_activeUser, function (b) { return b })
          // const _rate= (Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          // const rate= (Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          // data.participation=rate
          // data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          // data.compareRecognition=(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          // data.comparevalue_aligned=(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          // data.percent_value_aligned=(((data.value_aligned*100)/data.totalRecognition).toFixed())
          // data.comparepercent_value_aligned=(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
        }else if(value=="This Quarter"){
          
          const pastQuarter = format(addQuarters(companyNow,-1), 'YYYY-Q')
          const splitQuarter:any = pastQuarter.split('-')
          const thisquarter = format(companyNow, 'YYYY-Q')
          let firstDaypastquarter=''
          if(splitQuarter[1]==1){
            const date=`${pastQuarter.split('-')[0]}-01-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==2){
            const date=`${pastQuarter.split('-')[0]}-04-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==3){
            const date=`${pastQuarter.split('-')[0]}-07-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==4){
            const date=`${pastQuarter.split('-')[0]}-10-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          
          const firstDayquarter = format(startOfQuarter(companyNow), 'YYYY-MM-DD')
          let Difference_In_Time =companyNow.getTime()-new Date(firstDaypastquarter).getTime() 
          let Difference_In_Days =Math.round(Difference_In_Time / (1000 * 3600 * 24));
          let first_week=''
          let firstWeekQuarter = format(new Date(firstDayquarter), 'YYYY-W')
          let firstWeekToday = format(companyNow, 'YYYY-W')
          for(let i=0;i<=Difference_In_Days;i++){
            let date = format(addDays(companyNow,-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            const week = format(new Date(date), 'YYYY-W')
            if(first_week==''){
              first_week=week
            }
            let active_day=[]
            let given_day=[]
           
            if(findData&&date>=format(new Date(firstDayquarter), 'YYYY-MM-DD')){
              
              if(first_week!=week){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day=isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?"0": (Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }else if(firstWeekQuarter==firstWeekToday&&date==format(new Date(firstDayquarter), 'YYYY-MM-DD')){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                for(let i=1;i<12;i++){
                  const subweek=format(addWeeks(companyNow, -i), 'YYYY-W')
                  data.labels.push(subweek)
                  data.chartvalue_aligned.push('0')
                  data.chartRecognition.push('0')
                  data.chartparticipation.push('0')
                  data.chartpercent_value_aligned.push('0')
  
                }
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }
              
              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
              if(state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }
              
              
              data.totalRecognition=data.totalRecognition+findData.activity
              data.value_aligned=data.value_aligned+findData.hashtag_value
              
              dataChart.totalRecognition=dataChart.totalRecognition+findData.activity
              dataChart.value_aligned=dataChart.value_aligned+findData.hashtag_value
              
             
               

               
              
            }else if(findData){
              if(state.data_all_day_participation.activeUserAll[date]){
                //console.log('state.data_all_day_participation.activeUserAll[date]',state.data_all_day_participation.activeUserAll[date][0].users)
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              if(state.data_all_day_participation.givenAll[date]){
                //data.givenUser.push(...state.data_all_day_participation.givenAll[date].users)
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(date>=format(new Date(firstDayquarter), 'YYYY-MM-DD')){
              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              if(first_week!=week){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }else if(firstWeekQuarter==firstWeekToday&&date==format(new Date(firstDayquarter), 'YYYY-MM-DD')){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
                for(let i=1;i<12;i++){
                  const subweek=format(addWeeks(companyNow, -i), 'YYYY-W')
                  data.labels.push(subweek)
                  data.chartvalue_aligned.push('0')
                  data.chartRecognition.push('0')
                  data.chartparticipation.push('0')
                  data.chartpercent_value_aligned.push('0')
  
                }
              }
             
              
            
            }else {
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
            }
            
          }
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()

        }else if(value=="Last Quarter"){
          const pastQuarter = format(addQuarters(companyNow,-1), 'YYYY-Q')
          const splitQuarter:any = pastQuarter.split('-')
          let firstDaypastquarter=''
          let lastDaypastquarter=''
          if(splitQuarter[1]==1){
            const date=`${pastQuarter.split('-')[0]}-01-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
            const _date=`${pastQuarter.split('-')[0]}-03-31`
            lastDaypastquarter = format(new Date(_date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==2){
            const date=`${pastQuarter.split('-')[0]}-04-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
            const _date=`${pastQuarter.split('-')[0]}-06-30`
            lastDaypastquarter = format(new Date(_date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==3){
            const date=`${pastQuarter.split('-')[0]}-07-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
            const _date=`${pastQuarter.split('-')[0]}-09-30`
            lastDaypastquarter = format(new Date(_date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==4){
            const date=`${pastQuarter.split('-')[0]}-10-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
            const _date=`${pastQuarter.split('-')[0]}-12-31`
            lastDaypastquarter = format(new Date(_date), 'YYYY-MM-DD')
          }
          const pastlastQuarter = format(addQuarters(companyNow,-2), 'YYYY-Q')
          const splitlastQuarter:any = pastlastQuarter.split('-')
          let firstDaypastlastquarter=''
          if(splitlastQuarter[1]==1){
            const date=`${pastlastQuarter.split('-')[0]}-01-01`
            firstDaypastlastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitlastQuarter[1]==2){
            const date=`${pastlastQuarter.split('-')[0]}-04-01`
            firstDaypastlastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitlastQuarter[1]==3){
            const date=`${pastlastQuarter.split('-')[0]}-07-01`
            firstDaypastlastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitlastQuarter[1]==4){
            const date=`${pastlastQuarter.split('-')[0]}-10-01`
            firstDaypastlastquarter = format(new Date(date), 'YYYY-MM-DD')
          }

          let Difference_In_Time =new Date(lastDaypastquarter).getTime()-new Date(firstDaypastlastquarter).getTime() 
          let Difference_In_Days =Math.round(Difference_In_Time / (1000 * 3600 * 24));

          let first_week=''
          for(let i=0;i<=Difference_In_Days;i++){
            let date = format(addDays(new Date(lastDaypastquarter ),-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            const week = format(new Date(date), 'YYYY-W')
            if(first_week==''){
              first_week=week
            }
            
            
            if(findData&&date>=format(new Date(firstDaypastquarter), 'YYYY-MM-DD')){

              let active_day=[]
              let given_day=[]
              if(first_week!=week){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }

              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
              if(state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }
              
              
              data.totalRecognition=data.totalRecognition+findData.activity
              data.value_aligned=data.value_aligned+findData.hashtag_value
              
              dataChart.totalRecognition=dataChart.totalRecognition+findData.activity
              dataChart.value_aligned=dataChart.value_aligned+findData.hashtag_value

            }else if(findData){
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              if(state.data_all_day_participation.givenAll[date]){
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(date>=format(new Date(firstDaypastquarter), 'YYYY-MM-DD')){

              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              if(first_week!=week){
                const  active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                const given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }
            
            }else {
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
            }
          }
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
        }else if(value=="Past year"){
          let first_MM=''
          for(let i=0;i<=730;i++){
            let date = format(addDays(companyNow,-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            const MM = format(new Date(date), 'YYYY-MM')
            if(first_MM==''){
              first_MM=MM
            }
            
            if(findData&&date>=format(addDays(companyNow,-365), 'YYYY-MM-DD')){
              let active_day=[]
              let given_day=[]
              if(first_MM!=MM){
              active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
              given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
              const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
              data.chartvalue_aligned.push(dataChart.value_aligned)
              data.chartRecognition.push(dataChart.totalRecognition)
              data.chartparticipation.push(_rate_day)
              data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
              data.labels.push(first_MM)
              first_MM=MM
              dataChart={
                totalRecognition:0,
                labels:[],
                value_aligned:0,
                percent_value_aligned:'',
                activeUser:[],
                givenUser:[],
                participation:'',
              };
              }
              if(state.data_all_day_participation.activeUserAll[date]){
                
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
              if(state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }
              
              
              data.totalRecognition=data.totalRecognition+findData.activity
              data.value_aligned=data.value_aligned+findData.hashtag_value
              
              dataChart.totalRecognition=dataChart.totalRecognition+findData.activity
              dataChart.value_aligned=dataChart.value_aligned+findData.hashtag_value
               
              
            }else if(findData){
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              if(state.data_all_day_participation.givenAll[date]){
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(date>=format(addDays(companyNow,-365), 'YYYY-MM-DD')){
              if(first_MM!=MM){
                const active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                const given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_MM)
                first_MM=MM
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
                }
            }
          }
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()

          

        }
        data.labels.reverse()
        data.chartRecognition.reverse()
        data.chartvalue_aligned.reverse()
        data.chartpercent_value_aligned.reverse()
        data.chartparticipation.reverse()
        commit('setDataOverAll',data)
        commit('setChartLoading',false) 
      },
      async getOverAllCustomRange({commit,rootState,dispatch,state}:any, value:any){
        const { startDate, endDate } = value;
        
        let activeUser:any=[]
        let givenUser:any=[]
        let past_activeUser:any=[]
        let past_givenUser:any=[]
        const data:any={
          totalRecognition:0,
          chartRecognition:[],
          labels:[],
          pasttotalRecognition:0,
          compareRecognition:'',
          value_aligned:0,
          pastvalue_aligned:0,
          comparevalue_aligned:'',
          chartvalue_aligned:[],
          percent_value_aligned:'',
          comparepercent_value_aligned:'',
          chartpercent_value_aligned:[],
          participation:'',
          chartparticipation:[],
          past_participation:''
        };
        let dataChart:any={
          totalRecognition:0,
          labels:[],
          value_aligned:0,
          percent_value_aligned:'',
          activeUser:[],
          givenUser:[],
          participation:'',
        };
        
        // Calculate the date range
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        // Calculate the comparison period (same duration before the start date)
        const pastEnd = new Date(start);
        pastEnd.setDate(pastEnd.getDate() - 1);
        const pastStart = new Date(pastEnd);
        pastStart.setDate(pastStart.getDate() - diffDays);
        
        // Iterate through all days from past period start to current period end
        const totalDaysToProcess = diffDays * 2 + 2;
        const currentPeriodStart = format(start, 'YYYY-MM-DD');
        const currentPeriodEnd = format(end, 'YYYY-MM-DD');
        const pastPeriodStart = format(pastStart, 'YYYY-MM-DD');
        const pastPeriodEnd = format(pastEnd, 'YYYY-MM-DD');
        
        // Group data by month for chart display
        let first_month = '';
        
        // Process all dates in both periods
        for(let i = totalDaysToProcess; i >= 0; i--){
          let date = format(addDays(end, -i), 'YYYY-MM-DD');
          const findData = _.find(state.data_all_day, { date: date });
          const month = format(new Date(date), 'YYYY-MM');
          
          if(first_month === ''){
            first_month = month;
          }
          
          // Check if date is in current period
          const isCurrentPeriod = date >= currentPeriodStart && date <= currentPeriodEnd;
          // Check if date is in past period
          const isPastPeriod = date >= pastPeriodStart && date <= pastPeriodEnd;
          
          if(isCurrentPeriod){
            if(state.data_all_day_participation && state.data_all_day_participation.activeUserAll && state.data_all_day_participation.activeUserAll[date]){
              dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users);
              activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users);
            }
            
            // Check if we've moved to a new month
            if(first_month !== month){
              const active_day = _.groupBy(dataChart.activeUser, function (b) { return b });
              const given_day = _.groupBy(dataChart.givenUser, function (b) { return b });
              const _rate_day = isNaN(Object.keys(given_day).length*100 / Object.keys(active_day).length) ? '0' : (Object.keys(given_day).length*100 / Object.keys(active_day).length).toFixed();
              data.chartvalue_aligned.push(dataChart.value_aligned);
              data.chartRecognition.push(dataChart.totalRecognition);
              data.chartparticipation.push(_rate_day);
              data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition) ? '0' : ((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed());
              data.labels.push(first_month);
              first_month = month;
              dataChart = {
                totalRecognition: 0,
                labels: [],
                value_aligned: 0,
                percent_value_aligned: '',
                activeUser: [],
                givenUser: [],
                participation: '',
              };
            }
            
            if(findData){
              if(state.data_all_day_participation && state.data_all_day_participation.givenAll && state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users);
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users);
              }
              
              data.totalRecognition = data.totalRecognition + findData.activity;
              data.value_aligned = data.value_aligned + findData.hashtag_value;
              
              dataChart.totalRecognition = dataChart.totalRecognition + findData.activity;
              dataChart.value_aligned = dataChart.value_aligned + findData.hashtag_value;
            }
          } else if(isPastPeriod && findData){
            if(state.data_all_day_participation && state.data_all_day_participation.activeUserAll && state.data_all_day_participation.activeUserAll[date]){
              past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users);
            }
            if(state.data_all_day_participation && state.data_all_day_participation.givenAll && state.data_all_day_participation.givenAll[date]){
              past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users);
            }
            
            data.pasttotalRecognition = data.pasttotalRecognition + findData.activity;
            data.pastvalue_aligned = data.pastvalue_aligned + findData.hashtag_value;
          }
        }
        
        // Push the last month's data
        if(dataChart.totalRecognition > 0 || dataChart.value_aligned > 0){
          const active_day = _.groupBy(dataChart.activeUser, function (b) { return b });
          const given_day = _.groupBy(dataChart.givenUser, function (b) { return b });
          const _rate_day = isNaN(Object.keys(given_day).length*100 / Object.keys(active_day).length) ? '0' : (Object.keys(given_day).length*100 / Object.keys(active_day).length).toFixed();
          data.chartvalue_aligned.push(dataChart.value_aligned);
          data.chartRecognition.push(dataChart.totalRecognition);
          data.chartparticipation.push(_rate_day);
          data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition) ? '0' : ((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed());
          data.labels.push(first_month);
        }
        
        // Calculate participation rates
        const given = _.groupBy(givenUser, function (b) { return b });
        const active = _.groupBy(activeUser, function (b) { return b });
        const _given = _.groupBy(past_givenUser, function (b) { return b });
        const _active = _.groupBy(past_activeUser, function (b) { return b });
        const _rate = isNaN(Object.keys(_given).length*100 / Object.keys(_active).length) ? '0' : (Object.keys(_given).length*100 / Object.keys(_active).length).toFixed();
        const rate = isNaN(Object.keys(given).length*100 / Object.keys(active).length) ? '0' : (Object.keys(given).length*100 / Object.keys(active).length).toFixed();
        
        data.participation = rate;
        data.past_participation = (parseFloat(rate) - parseFloat(_rate)).toFixed(0);
        data.compareRecognition = data.pasttotalRecognition == 0 ? '0' : (((data.totalRecognition - data.pasttotalRecognition) * 100) / data.pasttotalRecognition).toFixed();
        data.comparevalue_aligned = data.pastvalue_aligned == 0 ? '0' : isNaN(((data.value_aligned - data.pastvalue_aligned) * 100) / data.pastvalue_aligned) ? '0' : (((data.value_aligned - data.pastvalue_aligned) * 100) / data.pastvalue_aligned).toFixed();
        data.percent_value_aligned = isNaN(((data.value_aligned * 100) / data.totalRecognition)) ? '0' : (((data.value_aligned * 100) / data.totalRecognition).toFixed());
        data.comparepercent_value_aligned = isNaN(parseFloat(data.percent_value_aligned) - ((data.pastvalue_aligned * 100) / data.pasttotalRecognition)) ? '0' : (parseFloat(data.percent_value_aligned) - ((data.pastvalue_aligned * 100) / data.pasttotalRecognition)).toFixed();
        
        // Reverse arrays to show chronological order
        data.labels.reverse();
        data.chartRecognition.reverse();
        data.chartvalue_aligned.reverse();
        data.chartpercent_value_aligned.reverse();
        data.chartparticipation.reverse();
        
        commit('setDataOverAll', data);
        commit('setChartLoading', false);
      }

      
    },
    getters: {
     activity : (state:any) => {
      
        return state.activity
      },
      activityall : (state:any) => {
        return state.activityall
      },
      durationActivityall : (state:any) => {
        return state.durationActivityall
      },
      results : (state:any) => {
        return state.results
      },
      sizepage: (state:any) => {
        return state.size_page
      },
      page : (state:any) => {
        return state.page
      },
      list_month: (state: any) => {
        return state.list_month
      },
      reportActivity:(state: any) => {
        return state.reportActivity
      },
      barchart:(state: any) => {
        return state.barchart
      },
      chartLoading:(state: any) => {
        return state.chartLoading
      },
      metricsLoading:(state: any) => {
        return state.metricsLoading
      },
      radarchart:(state: any) => {
        return state.radarchart
      },
      listChartmonth:(state: any) => {
        return state.listChartmonth
      },
      rewardTypeOptions:(state: any) => {
        return state.medal
      },
      dataOverAll:(state: any) => {
        return state.dataOverAll
      },
      radarchartAll:(state: any) => {
        return state.radarchartAll
      }
    }
  }
